/**
 * Os funcionários sem empresa — T11 e §10 da spec #408.
 *
 * O problema vira tela, não portão: em vez de barrar o cadastro órfão, quem opera a Plataforma
 * o vê listado dentro da própria tela de funcionários. Fora da Plataforma o bloco não existe —
 * o recorte de tenant já não alcança funcionário sem dono, e anunciá-lo ali seria falar de um
 * registro que a pessoa não pode abrir.
 */

import React, { useEffect, useState } from 'react';
import { Alert, AlertTitle, Box, Typography } from '@mui/material';

import { employeeService } from '../../services/employeeService';
import { useManifesto } from '../../stores/manifestoStore';
import type { Employee } from '../../types/employee';
import { formatCPF, formatDate } from '../../utils/employeeFormatters';
import { DataTable, type DataTableColumn } from '../common';

const COLUNAS: DataTableColumn<Employee>[] = [
  { id: 'name', label: 'Nome', render: (linha) => linha.name, primary: true },
  { id: 'cpf', label: 'CPF', render: (linha) => formatCPF(linha.cpf) },
  { id: 'createdAt', label: 'Data de cadastro', render: (linha) => formatDate(linha.createdAt) },
  {
    id: 'updatedAt',
    label: 'Última movimentação',
    render: (linha) => formatDate(linha.updatedAt),
  },
];

const VAZIO = {
  title: 'Nenhum funcionário sem empresa',
  description: 'Todo funcionário cadastrado nesta plataforma está ligado a uma empresa.',
};

/** O título conta quantos são: é o número que mede o tamanho da anomalia. */
function tituloDaFaixa(quantos: number): string {
  return quantos === 1
    ? '1 funcionário não está ligado a nenhuma empresa'
    : `${quantos} funcionários não estão ligados a nenhuma empresa`;
}

export const EmployeesWithoutCompanyBlock: React.FC = () => {
  const { manifesto } = useManifesto();
  const naPlataforma = manifesto?.perspectiva === 'plataforma';

  const [orfaos, setOrfaos] = useState<Employee[]>([]);

  useEffect(() => {
    if (!naPlataforma) return;

    let ativo = true;
    // Bloco acessório: falhar aqui não pode derrubar a listagem principal de funcionários.
    employeeService
      .getEmployeesWithoutCompany()
      .then((lista) => {
        if (ativo) setOrfaos(lista);
      })
      .catch(() => {
        if (ativo) setOrfaos([]);
      });

    return () => {
      ativo = false;
    };
  }, [naPlataforma]);

  // Enquanto a consulta não volta, a lista está vazia e o bloco não existe — sem esqueleto:
  // anunciar "carregando funcionários sem empresa" cria um problema antes de saber se ele há.
  if (!naPlataforma || orfaos.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mb: 3 }}>
      <Alert severity='warning' sx={{ mb: 2 }}>
        <AlertTitle>{tituloDaFaixa(orfaos.length)}</AlertTitle>

        <Typography variant='body2'>
          Eles não aparecem para cliente nenhum e não entram em convocação, exame nem
          faturamento.
        </Typography>
      </Alert>

      <DataTable
        columns={COLUNAS}
        rows={orfaos}
        getRowId={(linha) => linha.id}
        emptyState={VAZIO}
      />
    </Box>
  );
};

export default EmployeesWithoutCompanyBlock;
